import { ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useDidMount, useShowHide, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { FormHelper } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/helpers';
import { useReactiveForm } from '@hooks';
import { modals } from '@modals';
import { saveUserInput, useIsOperator, useUserInput } from '@store';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

interface ChangeElectronicMailOutput {
	resources: ChangeElectronicMailResources;
	isValid: boolean;
	isOperator: boolean;
	getInputProps: (name: string) => void;
	onClearRequest: () => void;
	onClickConfirm: () => void;
}

interface ChangeElectronicMailResources {
	title: string;
	subtitle: string;
	labelInput: string;
	back: string;
	confirm: string;
	errorMessage: ErrorMessage;
}

interface ErrorMessage {
	required: string;
	invalidEmail: string;
}

export const useChangeElectronicMailHelper = (): ChangeElectronicMailOutput => {
	const t = useTranslation();
	const { hide } = useShowHide(modals.changeElectronicMail);
	const { hide: hideValidationModal, show: showValidationModal } = useShowHide(modals.validation);
	const dispatch = useDispatch();
	const { email } = useUserInput();
	const isOperator = useIsOperator();
	const resources = useMemo((): ChangeElectronicMailResources => {
		let title = t('modals.changeEmail.title');
		let subtitle = t('modals.changeEmail.subtitle');
		if (isOperator) {
			title = t('modals.changeEmail.operator.title');
			subtitle = t('modals.changeEmail.operator.subtitle');
		}
		return {
			title,
			subtitle,
			labelInput: t('general.email'),
			back: t('modals.close.buttons.cancel'),
			confirm: t('modals.close.buttons.confirm'),
			errorMessage: {
				required: t('general.errors.required'),
				invalidEmail: t('general.errors.invalidEmail'),
			},
		};
	}, [t]);

	useDidMount(() => {
		if (!isOperator) {
			dispatch(saveUserInput({ isValid: email === undefined ? false : true }));
		}
	});

	const onClickConfirm = () => {
		if (isOperator) {
			/*
			Call Service will showValidationModal
			On response will hideValidationModal
			and show alert with response of service
			*/
			showValidationModal();
			hideValidationModal();
		} else {
			saveUserInputDispatch();
		}
		onClearRequest();
	};
	const saveUserInputDispatch = () => {
		dispatch(saveUserInput({ email: getInputProps('email').value, isValid: isValid }));
	};

	const form = useForm('changeEmailForm');
	const { getInputProps, isValid, clear } = useReactiveForm({
		name: form.name,
		onSubmit: onClickConfirm,
		validate(values) {
			return {
				email: FormHelper.validateField(values.email, [
					ValidationHelper.validateRequired(resources.errorMessage.required),
					ValidationHelper.validateEmail(resources.errorMessage.invalidEmail),
				]),
			};
		},
	});

	const onClearRequest = useCallback(() => {
		clear();
		hide();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { resources, isValid, isOperator, onClickConfirm, onClearRequest, getInputProps };
};
